import { useState, useEffect } from "react";
import { AccountLogin } from "./AccountLogin";
import { AccountEmail } from "./AccountEmail";
import { Header } from "./Header";
import { ProgressBar } from "./ui/ProgressBar";
import { enrichKlaviyoProfile, subscribeToKlaviyoList, trackStartedQuestionnaire } from "../lib/klaviyo-subscribe";
import type { ProgressSectionId } from "../lib/progress-calculator";
import {
  type GenderAudience,
  canCustomerPurchaseProduct,
  customerTagsToGenderRole,
  alertGenderPurchaseMismatch,
} from "../lib/gender-purchase-eligibility";

export interface AccountData {
  customer_id?: string; // MDI patient ID
  shopify_customer_id?: string; // Shopify customer ID
  shopifyCustomerTags?: string[];
  email: string;
  firstName?: string;
  lastName?: string;
  state?: string;
  phone?: string;
  sms_marketing_accept?: boolean | string;
  customerAccessToken?: string | null; // Shopify customer access token for cart association
  password?: string; // Keep password for future auth if needed
  has_shopify_account?: boolean; // Flag indicating Shopify account exists
  has_mdi_account?: boolean; // Flag indicating MDI account exists
  justRegistered?: boolean; // Set by AccountRegister only, for a brand-new registration —
  // nothing exists server-side yet, so a progress fetch immediately after would only find nothing
  progressJustChecked?: boolean; // Set once handleAccountDetailsComplete's own one-off
  dob?: string; // YYYY-MM-DD
  sex?: string;
  tnc?: boolean;
}

export interface AccountScreenProps {
  onContinue?: (data?: any) => void; // proceed to questionnaire
  onBack?: () => void;
  onClose?: () => void;
  logoUrl?: string;
  language?: "en" | "es";
  currentStep?: number;
  totalSteps?: number;
  sectionIndex?: number;
  totalSections?: number;
  sectionId?: ProgressSectionId;
  onSubStepChange?: (subStep: "email" | "login") => void;
  initialSubStep?: "email" | "login";
  initialEmail?: string;
  initialMdiCustomerId?: string;
  productGenderAudience?: GenderAudience;
  initialAccountData?: AccountData | null;
}

export function AccountScreen({
  onContinue,
  onBack,
  onClose,
  logoUrl,
  language = "en",
  currentStep,
  totalSteps,
  sectionIndex,
  totalSections,
  sectionId,
  onSubStepChange,
  initialSubStep,
  initialEmail,
  initialMdiCustomerId,
  productGenderAudience = "all",
  initialAccountData,
}: AccountScreenProps) {
  const portalElement = document.querySelector('[data-app-layer-portal="form-portal"]') as HTMLElement | null;
  const isShopifyLoggedIn = portalElement?.dataset.customerLoggedIn === "true";
  const customerEmail = portalElement?.dataset.customerEmail || "";
  const existingCustomerId = portalElement?.dataset.customerId || null;
  const productTitle = portalElement?.dataset.productTitle || "";

  const [step, setStep] = useState<"email" | "login">(initialSubStep ?? "email");
  const [email, setEmail] = useState(
    initialAccountData?.email || initialEmail || customerEmail
  );
  const [loading, setLoading] = useState(false);
  const [customerId, setCustomerId] = useState<string | null>(
    existingCustomerId || initialAccountData?.shopify_customer_id || null
  );
  const [mdiCustomerId, setMdiCustomerId] = useState<string | null>(
    initialMdiCustomerId || initialAccountData?.customer_id || null
  );
  const [tnc, setTnc] = useState(initialAccountData?.tnc === true);

  useEffect(() => {
    if (initialSubStep) {
      setStep(initialSubStep);
    }
  }, [initialSubStep]);

  // Notify parent when sub-step changes
  useEffect(() => {
    onSubStepChange?.(step);
  }, [step, onSubStepChange]);

  const handleAccountComplete = async (accountData?: any) => {
    // Best-effort — only fires once a name is known (e.g. a returning MDI customer's details
    // were just fetched on login). Uses enrichKlaviyoProfile, not identifyOnAccountCreation:
    // this is a returning customer, so a Klaviyo profile already exists — enriching it here
    // must never re-fire "Account Created".
    await enrichKlaviyoProfile(accountData ?? {});
    onContinue?.(accountData);
  };

  useEffect(() => {
    if (isShopifyLoggedIn && !customerId) {
      fetch("/apps/rx/customer", {
        method: "GET",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
      })
        .then((res) => res.json())
        .then((data) => {
          setCustomerId(data.customer_id ?? null);
        })
        .catch((err) => console.error("Failed to fetch customer info:", err));
    }
  }, [isShopifyLoggedIn, customerId]);

  // When the user submits their email, decide next step
  const handleEmailContinue = async (emailValue: string, tncValue: boolean) => {
    setEmail(emailValue);
    setTnc(tncValue);
    setLoading(true);

    // Fire-and-forget — consent was just given on this screen, so subscribe regardless of
    // which branch (new registration vs existing-customer login) follows. Never blocks the flow.
    subscribeToKlaviyoList({ email: emailValue }).catch(() => {});

    // SRX-263: "Started Questionnaire" must follow the initial Klaviyo event immediately — this
    // is the flow's abandonment trigger, replacing the old backend-app hourly poll that broke
    // when SRX-242 moved account creation to the end of the form.
    trackStartedQuestionnaire({
      email: emailValue,
      productUrl: typeof window !== 'undefined' ? window.location.href : undefined,
      productName: productTitle,
    }).catch(() => {});

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

      const response = await fetch(`/apps/rx/customer`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: emailValue }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const customerStatus = await response.json();

      if (Array.isArray(customerStatus.tags) && customerStatus.tags.length > 0) {
        const role = customerTagsToGenderRole(customerStatus.tags as string[]);
        if (!canCustomerPurchaseProduct(role, productGenderAudience)) {
          alertGenderPurchaseMismatch(language);
          setLoading(false);
          return;
        }
      }

      const hasShopify = !!customerStatus.shopify;
      const hasMDI = !!customerStatus.mdi;

      // Store MDI customer_id if it exists (mdi field IS the customer_id)
      if (hasMDI && customerStatus.mdi) {
        setMdiCustomerId(customerStatus.mdi);
      }

      if (hasShopify) {
        // Exists in Shopify (with or without MDI) → log in to get their password/token first
        setStep("login");
      } else {
        // No Shopify account — continue straight through. Personal/account details
        // (name, DOB, sex, phone, password) are collected later, in account-details,
        // after the customer has chosen their treatment.
        onContinue?.({
          email: emailValue,
          has_shopify_account: false,
          has_mdi_account: hasMDI,
          tnc: tncValue,
        });
      }
    } catch (error) {
      console.error("[AccountScreen] Error checking customer:", error);
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          alert("Request timed out. Please check your connection and try again.");
        } else {
          alert("There was a problem checking your account. Please try again.");
        }
      } else {
        alert("There was a problem checking your account. Please try again.");
      }
    } finally {
      setLoading(false); // stop loading regardless of success or fail
    }
  };

  // Header back: from login, return to email; from email, exit to whatever precedes this screen
  const handleHeaderBack = () => {
    if (step === "login") {
      setStep("email");
    } else {
      onBack?.();
    }
  };

  return (
    <div className="w-full min-h-full flex flex-col bg-gradient-to-b from-[#FAFAFA] to-[#E8F0F3]">
      <Header logoUrl={logoUrl} onBack={handleHeaderBack} onClose={onClose} />

      <div className="max-w-[640px] flex flex-col h-[calc(100%-40px)] mx-auto px-5 py-[30px] lg:px-4 w-full mb-0">
        {currentStep !== undefined &&
          totalSteps !== undefined &&
          sectionIndex !== undefined &&
          totalSections !== undefined &&
          sectionId !== undefined && (
          <ProgressBar
            currentStep={currentStep}
            totalSteps={totalSteps}
            sectionIndex={sectionIndex}
            totalSections={totalSections}
            sectionId={sectionId}
            language={language}
          />
        )}

        {step === "email" && (
          <AccountEmail
            onContinue={handleEmailContinue}
            language={language}
            loading={loading}
          />
        )}

        {step === "login" && (
          <AccountLogin
            email={email}
            onBack={() => setStep("email")}
            // When login succeeds, continue to questionnaire:
            onContinue={(data) => handleAccountComplete(data)}
            language={language}
            mdiCustomerId={mdiCustomerId}
            productTitle={productTitle}
            productGenderAudience={productGenderAudience}
            tnc={tnc}
          />
        )}
      </div>
    </div>
  );
}
